import jwtDecode, { JwtPayload } from 'jwt-decode';

import { AuthFactor } from '../api/enumerations';
import { LoginProps } from '../api/login/types';

/** Which screen completes the second factor for this pending login. */
export type MfaMode = 'setup' | 'totp' | 'otp';

export interface MfaPendingSession {
  token: string;
  mode: MfaMode;
}

const mfaModes: Record<MfaMode, true> = { setup: true, totp: true, otp: true };

interface MfaClaims extends JwtPayload {
  /**
   * Set by the API on a pre-verification token. Absent on an older backend,
   * which is what keeps this front safe to deploy first (REAL-3264).
   */
  mfa_required?: boolean;
}

const decodeClaims = (token: string): MfaClaims | null => {
  try {
    return jwtDecode<MfaClaims>(token);
  } catch {
    return null;
  }
};

const isPendingToken = (accessToken: string): boolean =>
  decodeClaims(accessToken)?.mfa_required === true;

/**
 * A pending token lasts 30 min; past that every MFA call answers 401, so the
 * session it belongs to cannot be completed any more.
 */
const isSpentToken = (token: string): boolean => {
  const claims = decodeClaims(token);
  // Unreadable: no MFA call would accept it either.
  if (!claims) return true;

  return claims.exp !== undefined && Date.now() / 1000 > claims.exp;
};

const isMfaMode = (value: unknown): value is MfaMode =>
  typeof value === 'string' && value in mfaModes;

/**
 * Whether a stored entry still describes a login that can be finished: right
 * shape, and a token the API would still answer to.
 */
export const isUsableMfaPendingSession = (
  value: unknown
): value is MfaPendingSession => {
  if (typeof value !== 'object' || value === null) return false;

  const { token, mode } = value as Partial<MfaPendingSession>;
  return (
    typeof token === 'string' &&
    token.length > 0 &&
    isMfaMode(mode) &&
    !isSpentToken(token)
  );
};

/**
 * Which second factor screen this login needs, or null for a full session.
 *
 * The token itself is the source of truth for "is this pending": the response
 * fields only pick WHICH screen. An enforced user with no TOTP yet — including
 * one still on OTP, which does not satisfy the requirement — goes to the setup.
 */
export const resolveMfaMode = (response: LoginProps): MfaMode | null => {
  if (!response.access_token || !isPendingToken(response.access_token)) {
    return null;
  }
  if (response.mfa_setup_required) return 'setup';
  if (response.auth_factor === AuthFactor.TOTP) return 'totp';
  if (response.auth_factor === AuthFactor.OTP) return 'otp';

  // Pending without a configured factor: enrollment is the only way through.
  return 'setup';
};
